/**
 * Homebrew Subclass Substrate
 *
 * Shared helpers for folding homebrew subclass features into SRD class data,
 * slugifying subclass names, and filtering class features by the selected
 * subclass. Consumed by the engine, server-srd, builder, and client-srd.
 *
 * D&D 5e System Reference Document 5.1
 * Licensed under Creative Commons Attribution 4.0 International (CC-BY-4.0)
 */

import type { SrdClass, SrdClassFeature } from './types.js';

/**
 * Normalize a subclass name into a URL-safe slug.
 *
 * Lowercases, trims, converts spaces/underscores to "-", strips characters
 * other than [a-z0-9-], and collapses repeated "-".
 *
 * Mirrors src/components/character-hud/builder-mode/builder-helpers.ts slugify
 * so results match across the codebase.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
}

/** A single homebrew subclass entry, keyed by slug with free-form data payload. */
export interface HbSubclassEntry {
  slug: string;
  name: string;
  data: Record<string, unknown>;
}

/** Shape of a raw homebrew subclass feature carried inside entry.data. */
interface RawSubclassFeature {
  name: string;
  level: number;
  description: string;
}

/**
 * Fold homebrew subclass features into the matching parent SRD classes.
 *
 * MUTATES the elements of the `classes` array (reassigns `cls.features`), but
 * NEVER mutates the shared feature array in place: a fresh array is assigned so
 * the shared module-constant SRD feature arrays are left untouched.
 *
 * For each entry: find the class whose slug === entry.data.parentClass, read
 * entry.data.subclassFeatures, and append them (mapped to SrdClassFeature and
 * tagged with subclass/subclassSlug). Entries whose parent class is not found
 * or that carry no subclass features are skipped.
 */
export function foldHomebrewSubclassFeatures(
  classes: SrdClass[],
  subclassEntries: HbSubclassEntry[],
): void {
  for (const entry of subclassEntries) {
    const parentClass = entry.data.parentClass;
    const cls = classes.find((c: SrdClass): boolean => c.slug === parentClass);
    if (!cls) continue;

    const rawFeatures = entry.data.subclassFeatures as RawSubclassFeature[] | undefined;
    if (!rawFeatures || rawFeatures.length === 0) continue;

    const mapped: SrdClassFeature[] = rawFeatures.map(
      (f: RawSubclassFeature): SrdClassFeature => ({
        name: f.name,
        level: f.level,
        description: f.description,
        subclass: entry.name,
        subclassSlug: entry.slug,
      }),
    );

    // Fresh array — NEVER push into cls.features (shared SRD module constant).
    cls.features = [...cls.features, ...mapped];
  }
}

/**
 * Decide whether a class feature applies given the selected subclass.
 *
 * Base class features (no `subclass`) always apply. Subclass features are
 * excluded when no subclass is chosen, and otherwise included only when the
 * feature's owning subclass slug matches the selected one.
 */
export function includeClassFeature(
  f: SrdClassFeature,
  selectedSubclassSlug: string | null,
): boolean {
  if (!f.subclass) return true;
  if (!selectedSubclassSlug) return false;
  return (f.subclassSlug ?? slugify(f.subclass)) === selectedSubclassSlug;
}